import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  CollectorListItem,
  CollectorsConfig
} from '../domain/collector-profile.model';
import { MIN_COLLECTOR_SEARCH_QUERY_LENGTH } from '../domain/user';
import { AlertService } from '../services/alert.service';
import { CollectorProfileService } from '../services/collector-profile.service';
import { getHttpErrorMessage } from '../shared/http-error-message.util';
import { BulkImportCollectorsDialogComponent } from './bulk-import-collectors-dialog/bulk-import-collectors-dialog.component';

const INITIAL_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

@Component({
  selector: 'app-collectors',
  templateUrl: './collectors.component.html',
  styleUrls: [
    '../shared/table-row-hover.shared.css',
    './collectors.component.css'
  ]
})
export class CollectorsComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    'expand',
    'name',
    'email',
    'team',
    'role',
    'score',
    'rejectionRate',
    'reliability',
    'engagement',
    'skills'
  ];
  readonly detailRowColumns = ['expandedDetail'];
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  dataSource = new MatTableDataSource<CollectorListItem>([]);
  loading = false;
  searchInputValue = '';
  exportingCsv = false;
  searchQuery = '';
  totalCount = 0;
  pageIndex = 0;
  pageSize = INITIAL_PAGE_SIZE;
  expandedCollectorAuth0Id: string | null = null;

  addCollectorAuth0Id = '';

  config: CollectorsConfig = {
    reliabilityOptions: ['HIGH', 'MEDIUM', 'LOW', 'NONE'],
    engagementOptions: [
      'NONE',
      'BACKUP',
      'LOW',
      'MEDIUM',
      'AVERAGE',
      'HIGH',
      'MAX'
    ],
    eventSkillOptions: ['EXPERT', 'HIGH', 'MEDIUM', 'LOW', 'NONE'],
    eventSkillCodes: [],
    eventSkillGroups: [],
    eventCombinationFields: [
      'FOINT+SHOT',
      'FOINT+VOT',
      'SHOT+VOT',
      'TOI+TOI',
      'FOINT+SHOT+VOT',
      'LIVE',
      'LIVELIGHT'
    ],
    teamOptions: [],
    roleOptions: []
  };

  private readonly searchChange$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private collectorProfileService: CollectorProfileService,
    private dialog: MatDialog,
    private alertService: AlertService
  ) {}

  async ngOnInit() {
    this.searchChange$
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        void this.applySearchFilter(query);
      });

    await this.loadCollectorsConfig();
    await this.loadCollectors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchChange$.complete();
  }

  onSearchChanged(query: string) {
    this.searchInputValue = query ?? '';
    this.searchChange$.next(this.searchInputValue);
  }

  onAddCollectorAuth0IdChanged(auth0Id: string) {
    this.addCollectorAuth0Id = auth0Id?.trim() ?? '';
  }

  async onPageChanged(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    await this.loadCollectors();
  }

  async addCollector() {
    const collectorAuth0Id = this.addCollectorAuth0Id.trim();
    if (!collectorAuth0Id) {
      return;
    }

    try {
      await firstValueFrom(
        this.collectorProfileService.addCollectorByAuth0Id(collectorAuth0Id)
      );
      this.alertService.showInfo('Collector added.');
      this.addCollectorAuth0Id = '';
      this.pageIndex = 0;
      await this.loadCollectors();
    } catch (error) {
      const errorMessage =
        error?.error?.message ?? error?.message ?? 'Could not add collector';
      this.alertService.showError(errorMessage);
    }
  }

  async openBulkImportDialog() {
    const dialogRef = this.dialog.open(BulkImportCollectorsDialogComponent, {
      disableClose: true,
      width: '860px',
      maxWidth: '95vw',
      data: {
        config: this.config
      }
    });
    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result?.refreshed) {
      this.pageIndex = 0;
      await this.loadCollectors();
    }
  }

  async exportCollectorsCsv() {
    this.exportingCsv = true;
    try {
      const blob = await firstValueFrom(
        this.collectorProfileService.exportCollectorsCsv()
      );
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'collectors.csv';
      anchor.click();
      window.URL.revokeObjectURL(url);
      this.alertService.showInfo('Collector CSV export downloaded.');
    } catch (error) {
      const errorMessage = await getHttpErrorMessage(
        error,
        'Could not export collectors CSV'
      );
      this.alertService.showError(errorMessage);
    } finally {
      this.exportingCsv = false;
    }
  }

  toggleExpanded(collector: CollectorListItem) {
    if (this.expandedCollectorAuth0Id === collector.auth0Id) {
      this.expandedCollectorAuth0Id = null;
      return;
    }

    this.expandedCollectorAuth0Id = collector.auth0Id;
  }

  isExpanded(collector: CollectorListItem): boolean {
    return this.expandedCollectorAuth0Id === collector.auth0Id;
  }

  private async loadCollectorsConfig() {
    try {
      const config = await firstValueFrom(
        this.collectorProfileService.getConfig()
      );
      this.config = {
        ...this.config,
        ...config,
        teamOptions: [...(config?.teamOptions ?? [])].sort((left, right) =>
          left.localeCompare(right)
        ),
        roleOptions: [...(config?.roleOptions ?? [])].sort((left, right) =>
          left.localeCompare(right)
        )
      };
    } catch (error) {
      const errorMessage =
        error?.error?.message ??
        error?.message ??
        'Could not load collectors config';
      this.alertService.showError(errorMessage);
    }
  }

  private async applySearchFilter(query: string) {
    const trimmedQuery = query.trim();
    const normalizedQuery =
      trimmedQuery.length >= MIN_COLLECTOR_SEARCH_QUERY_LENGTH
        ? trimmedQuery
        : '';
    if (normalizedQuery === this.searchQuery) {
      return;
    }

    this.searchQuery = normalizedQuery;
    this.pageIndex = 0;
    await this.loadCollectors();
  }

  private async loadCollectors() {
    this.loading = true;
    this.expandedCollectorAuth0Id = null;
    try {
      const result = await firstValueFrom(
        this.collectorProfileService.listCollectors(
          this.pageIndex,
          this.pageSize,
          this.searchQuery
        )
      );
      this.totalCount = result?.count ?? 0;
      this.dataSource.data = result?.data ?? [];
    } catch (error) {
      this.totalCount = 0;
      this.dataSource.data = [];
      const errorMessage =
        error?.error?.message ??
        error?.message ??
        'Could not load collectors list';
      this.alertService.showError(errorMessage);
    } finally {
      this.loading = false;
    }
  }
}
